import { Dispatch, useMemo, useState } from 'react'
import { Grid, Divider, Image } from 'semantic-ui-react'
import Card from '../../../components/BaseComponents/Card'
import styles from './styles.module.scss'

import {
  GridRowColumn,
  Text,
  Checkbox,
  Icon,
} from '../../../components/BaseComponents'
import {
  isoToUTCDateTime,
  DATE_FORMATS_LUXON,
} from '../../../utils/dateHelpers'
import { MissingStatementCard } from './missingBankMonthUpload'
import { MissingStatementMultipleUploadCard } from './missingBankMonthMultipleUpload'
import {
  MissingStatement,
  UserDocument,
} from '../../UserDocuments/userDocuments.slice'
import DeleteDocumentConfirm from '../../UserDocuments/sharedComponents/Modals/DeleteDocumentConfirm'
import { InstitutionLogo } from '../../../components/Finances/Accounts/InstitutionCard'
import { PlaidItem } from '../../../reducers/finances/plaidItemReducer'
import { regular } from '@fortawesome/fontawesome-svg-core/import.macro'
import {
  NotExpectedState,
  NotExpectedStateAction,
} from '../UploadStatementsStage/types'

interface IMissingStatementsProps {
  missingStatements?: MissingStatement[]
  statements?: UserDocument[]
  plaidAccount?: PlaidItem
  isPlaid?: boolean
  notExpectedState?: NotExpectedState
  setNotExpectedValue?: Dispatch<NotExpectedStateAction>
}

interface ImonthYear {
  monthYear: string
  monthYearLabel: string
  accountId: number
}
interface IStatementAccount {
  [name: string]: {
    account: string
    accountId: number
    monthList: ImonthYear[]
  }
}
interface IAccount {
  name: string
  mask: string | null
  logo: string
  financial_institution_name: string | null
  financial_institution_id: string
  financialAccountId: number
}

export const MissingStatements = ({
  missingStatements = [],
  statements = [],
  plaidAccount,
  notExpectedState = {},
  setNotExpectedValue = (_: NotExpectedStateAction) => {},
  isPlaid = false,
}: IMissingStatementsProps) => {
  const [multipleUpload, setMultipleUpload] = useState(false)

  const { statementMonth, statementMonthLabel, accountObject } = useMemo(() => {
    const statementMonth: IStatementAccount = {}
    const statementMonthLabel: string[] = []
    let accountObject: IAccount | undefined

    if (missingStatements.length) {
      const {
        name,
        mask,
        logo,
        financial_institution_name,
        financial_institution_id,
        financialAccountId,
      } = missingStatements[0]
      accountObject = {
        name,
        mask,
        logo,
        financial_institution_name,
        financial_institution_id: financial_institution_id ?? '',
        financialAccountId: financialAccountId ?? 0,
      }
      for (const accountStatements of missingStatements) {
        if (
          Boolean(accountStatements.statements.length) ||
          Boolean(accountStatements.noStatementExpectedMonths.length)
        ) {
          const accountName = accountStatements.name.replace('Plaid ', '')
          statementMonth[accountStatements.name] = {
            account: `${accountStatements.financial_institution_name} ${accountName} ${accountStatements.mask}`,
            accountId: accountStatements.financialAccountId ?? 0,
            monthList: [],
          }
        }

        for (const statement of accountStatements.statements) {
          const monthYear = isoToUTCDateTime(statement).toFormat(
            DATE_FORMATS_LUXON.MONTH_YEAR_LONG
          )
          const yearMonth = isoToUTCDateTime(statement).toFormat(
            DATE_FORMATS_LUXON.YEAR_MONTH
          )
          const existingMonth = statementMonth[
            accountStatements.name
          ].monthList.find((month) => month.monthYear === yearMonth)

          if (!existingMonth) {
            statementMonth[accountStatements.name].monthList.push({
              monthYear: yearMonth,
              monthYearLabel: monthYear,
              accountId: accountStatements.financialAccountId ?? 0,
            })
          }

          if (!statementMonthLabel.includes(monthYear)) {
            statementMonthLabel.push(monthYear)
          }
        }

        for (const statement of accountStatements.noStatementExpectedMonths) {
          const monthYear = isoToUTCDateTime(statement).toFormat(
            DATE_FORMATS_LUXON.YEAR_MONTH
          )
          const monthYearLabel = isoToUTCDateTime(statement).toFormat(
            DATE_FORMATS_LUXON.MONTH_YEAR_LONG
          )
          statementMonth[accountStatements.name].monthList.push({
            monthYear,
            monthYearLabel,
            accountId: accountStatements.financialAccountId ?? 0,
          })
          setNotExpectedValue({
            financialAccountId: accountStatements.financialAccountId ?? 0,
            monthYear,
            value: true,
          })

          if (!statementMonthLabel.includes(monthYear)) {
            statementMonthLabel.push(monthYear)
          }
        }
      }
    }

    return {
      statementMonth,
      statementMonthLabel,
      accountObject,
    }
  }, [missingStatements, setNotExpectedValue])

  return !isPlaid ? (
    <Grid className={styles.financialInstitutionCard}>
      <Card>
        <Grid className={styles.headerGrid}>
          <Grid className={styles.financialAccountCard}>
            <Image
              src={accountObject?.logo}
              alt="heard logo"
              style={{ height: 40 }}
            />
            <Text as="h2">{accountObject?.financial_institution_name}</Text>
          </Grid>
          <Grid className={styles.financialHasMultipleUploads}>
            {statementMonthLabel.length > 0 && (
              <Checkbox
                className="checkbox"
                label="Upload statement with multiple months/accounts"
                onChange={() => setMultipleUpload(!multipleUpload)}
                checked={multipleUpload}
                variant="default"
                style={{ paddingRight: 0 }}
                labelStyle={{ fontSize: 12, paddingTop: 2, paddingRight: 0 }}
              />
            )}
          </Grid>
        </Grid>
        <GridRowColumn className={styles.missingStatementGrid}>
          <Divider />
          {Object.values(statementMonth).map(
            ({ account, monthList }) =>
              !multipleUpload && (
                <>
                  <div className={styles.accountIconGrid}>
                    {account.toLowerCase().includes('credit') && (
                      <Icon
                        color="gray"
                        icon={regular('credit-card')}
                        size="2x"
                      />
                    )}
                    {!account.toLowerCase().includes('credit') && (
                      <Icon color="gray" icon={regular('bank')} size="2x" />
                    )}
                    <Text>{String(account)}</Text>
                  </div>
                  {Array.isArray(monthList) &&
                    monthList.map(
                      ({ monthYearLabel, monthYear, accountId }) => (
                        <MissingStatementCard
                          key={monthYearLabel}
                          month={monthYearLabel}
                          yearMonth={monthYear}
                          financialAccountId={accountId}
                          statements={statements}
                          notExpected={Boolean(
                            notExpectedState[accountId]?.[monthYear]
                          )}
                          setNotExpectedValue={setNotExpectedValue}
                        />
                      )
                    )}
                </>
              )
          )}
          {multipleUpload && accountObject && (
            <MissingStatementMultipleUploadCard
              statementMonth={statementMonth}
              accountObject={accountObject}
              statements={statements}
            />
          )}
        </GridRowColumn>
      </Card>
      <DeleteDocumentConfirm />
    </Grid>
  ) : (
    <Grid className={styles.financialInstitutionCard}>
      <Card>
        <Grid className={styles.headerGrid}>
          <Grid className={styles.financialAccountCard}>
            {plaidAccount && (
              <InstitutionLogo
                institution={plaidAccount}
                className="institutionLogo"
              />
            )}

            <Text as="h2" style={{ paddingBottom: 16 }}>
              {plaidAccount?.institutionName}
            </Text>
          </Grid>
        </Grid>
        <GridRowColumn className={styles.noMissingStatements}>
          <Text>
            You don&apos;t need to do anything! This financial institution
            doesn&apos;t require statements for setup. We&apos;ll let you know
            if we encounter an issue.
          </Text>
        </GridRowColumn>
      </Card>
    </Grid>
  )
}

export default MissingStatements
